import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { History, Loader2, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const SYM = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
const fmtN = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("tr-TR") : "—");

/**
 * PriceHistoryDialog — ürünün geçmiş fiyat kayıtları (salt okunur).
 * "Fiyatı Kullan" yalnızca birim fiyat + para birimini üst forma taşır;
 * KDV/iskonto'ya dokunmaz, geçmiş belgeyi değiştirmez.
 */
export default function PriceHistoryDialog({ open, onOpenChange, productId, productName, customerId, onPick }) {
  const { can } = useAuth();
  const canSeePurchases = can("purchases", "view");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offsets, setOffsets] = useState({ sales: 0, customer_sales: 0, quotes: 0, purchases: 0 });
  const [acc, setAcc] = useState({ sales: [], customer_sales: [], quotes: [], purchases: [] });

  const load = useCallback(async (section, offset, append) => {
    if (!productId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "20", offset: String(offset) });
      if (customerId) params.set("customer_id", customerId);
      const { data: d } = await api.get(`/products/${productId}/price-history?${params}`);
      setData(d);
      if (append && section) {
        setAcc((prev) => ({ ...prev, [section]: [...(prev[section] || []), ...((d[section]?.items) || [])] }));
      } else {
        setAcc({
          sales: d.sales?.items || [],
          customer_sales: d.customer_sales?.items || [],
          quotes: d.quotes?.items || [],
          purchases: d.purchases?.items || [],
        });
      }
      setOffsets((prev) => ({ ...prev, ...(section ? { [section]: offset } : {}) }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [productId, customerId]);

  useEffect(() => {
    if (open && productId) {
      setOffsets({ sales: 0, customer_sales: 0, quotes: 0, purchases: 0 });
      load(null, 0, false);
    }
  }, [open, productId, load]);

  const pick = (row) => {
    onPick?.(Number(row.unit_price || 0), row.currency || "TRY");
    toast.success(`Fiyat aktarıldı: ${SYM[row.currency] || "₺"}${fmtN(row.unit_price)} (${row.currency})`);
    onOpenChange?.(false);
  };

  const table = (rows, cols, section, showCustomer) => (
    <div className="border border-slate-800 rounded-sm overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-950 border-b border-slate-800">
          <tr className="text-[10px] uppercase font-mono text-slate-500">
            <th className="text-left px-2 py-1.5">Tarih</th>
            {showCustomer && <th className="text-left px-2 py-1.5">Cari</th>}
            {cols.includes("doc") && <th className="text-left px-2 py-1.5">Belge No</th>}
            {cols.includes("supplier") && <th className="text-left px-2 py-1.5">Tedarikçi</th>}
            <th className="text-left px-2 py-1.5">Varyant</th>
            <th className="text-right px-2 py-1.5">Miktar</th>
            <th className="text-right px-2 py-1.5">Birim Fiyat</th>
            <th className="text-left px-2 py-1.5">PB</th>
            <th className="text-right px-2 py-1.5">Kur</th>
            <th className="text-right px-2 py-1.5">TL Karşılığı</th>
            {cols.includes("status") && <th className="text-left px-2 py-1.5">Durum</th>}
            <th className="text-right px-2 py-1.5"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800/60">
          {rows.map((r, i) => (
            <tr key={i} data-testid={`price-history-row-${section}-${i}`}>
              <td className="px-2 py-1.5 font-mono text-slate-400 whitespace-nowrap">{fmtDate(r.date)}</td>
              {showCustomer && <td className="px-2 py-1.5 text-slate-200 max-w-[140px] truncate">{r.customer || "—"}</td>}
              {cols.includes("doc") && <td className="px-2 py-1.5 font-mono text-blue-400 whitespace-nowrap">{r.doc_no || "—"}</td>}
              {cols.includes("supplier") && <td className="px-2 py-1.5 text-slate-200 max-w-[140px] truncate">{r.supplier || "—"}</td>}
              <td className="px-2 py-1.5 font-mono text-emerald-400 whitespace-nowrap">{r.variant || "—"}</td>
              <td className="px-2 py-1.5 text-right font-mono">{Number(r.qty || 0).toLocaleString("tr-TR")}</td>
              <td className="px-2 py-1.5 text-right font-mono font-semibold text-slate-100">{SYM[r.currency] || "₺"}{fmtN(r.unit_price)}</td>
              <td className="px-2 py-1.5 font-mono text-slate-400">{r.currency}</td>
              <td className="px-2 py-1.5 text-right font-mono text-slate-400">{r.fx_rate ? Number(r.fx_rate).toFixed(4) : "—"}</td>
              <td className="px-2 py-1.5 text-right font-mono text-slate-300">
                {r.try_unit_price != null ? `₺${fmtN(r.try_unit_price)}` : "—"}
              </td>
              {cols.includes("status") && (
                <td className="px-2 py-1.5">
                  <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded-sm border ${
                    r.status === "Bekliyor" ? "text-amber-300 border-amber-900 bg-amber-950/30"
                    : r.status === "Süresi Doldu" ? "text-slate-400 border-slate-700 bg-slate-900"
                    : "text-red-300 border-red-900 bg-red-950/30"
                  }`}>{r.status}</span>
                </td>
              )}
              <td className="px-2 py-1.5 text-right">
                <Button size="sm" data-testid={`price-use-${section}-${i}`}
                  onClick={() => pick(r)}
                  className="bg-blue-600 hover:bg-blue-500 rounded-sm h-6 text-[10px] uppercase tracking-wider font-semibold px-2">
                  <Check className="w-3 h-3 mr-1" /> Fiyatı Kullan
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const sectionBody = (section, rows, page, cols, showCustomer, emptyText) => (
    <div className="space-y-2">
      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-10 border border-slate-800 rounded-sm" data-testid={`price-history-empty-${section}`}>
          {emptyText}
        </div>
      ) : (
        <>
          {table(rows, cols, section, showCustomer)}
          {page?.has_more && (
            <div className="flex justify-center pt-1">
              <Button variant="outline" size="sm" disabled={loading}
                data-testid={`price-history-more-${section}`}
                onClick={() => load(section, offsets[section] + 20, true)}
                className="border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm text-xs">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `Daha Fazla Göster (${rows.length}/${page.total})`}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="price-history-dialog">
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono truncate">{productName}</div>
          <DialogTitle className="text-slate-50 tracking-tight flex items-center gap-2">
            <History className="w-5 h-5 text-blue-500" /> Önceki Fiyatlar
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="sales">
          <TabsList className="bg-slate-900 border border-slate-800 rounded-sm mb-4 flex-wrap h-auto">
            <TabsTrigger value="sales" data-testid="ph-tab-sales" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs">
              Önceki Satışlar ({data?.sales?.total ?? 0})
            </TabsTrigger>
            {customerId && (
              <TabsTrigger value="customer" data-testid="ph-tab-customer" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs">
                Bu Cariye Satışlar ({data?.customer_sales?.total ?? 0})
              </TabsTrigger>
            )}
            {canSeePurchases && (
              <TabsTrigger value="purchases" data-testid="ph-tab-purchases" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs">
                Önceki Alışlar ({data?.purchases?.total ?? 0})
              </TabsTrigger>
            )}
            <TabsTrigger value="quotes" data-testid="ph-tab-quotes" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs">
              Teklifler ({data?.quotes?.total ?? 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sales">
            {sectionBody("sales", acc.sales, data?.sales, [], true, "Bu ürün için satış geçmişi bulunamadı.")}
          </TabsContent>
          {customerId && (
            <TabsContent value="customer">
              {sectionBody("customer_sales", acc.customer_sales, data?.customer_sales, [], false, "Bu cariye bu üründen daha önce satış yapılmamış.")}
            </TabsContent>
          )}
          {canSeePurchases && (
            <TabsContent value="purchases">
              {sectionBody("purchases", acc.purchases, data?.purchases, ["supplier"], false, "Bu ürün için alış geçmişi bulunamadı.")}
            </TabsContent>
          )}
          <TabsContent value="quotes">
            {sectionBody("quotes", acc.quotes, data?.quotes, ["doc", "status"], true, "Bu ürün için geçmiş teklif bulunamadı.")}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

/** Küçük tetikleyici link — ürün seçili değilse disabled. */
export function PriceHistoryLink({ productId, onClick, testId = "price-history-link" }) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={!productId}
      onClick={onClick}
      className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono text-blue-400 hover:text-blue-300 disabled:text-slate-600 disabled:cursor-not-allowed transition-colors"
    >
      <History className="w-3 h-3" /> Önceki Fiyatlar
    </button>
  );
}
